import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Box,
  Tooltip,
  Switch
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Percent as PercentIcon,
  Assessment as ReportIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const OfferCard = ({ offer, onEdit, onDelete, onToggleActive, onViewStats }) => {
  const getTypeLabel = (type) => {
    const types = {
      percentage: 'Porcentaje',
      fixed_amount: 'Monto Fijo',
      '2x1': '2x1',
      buy_x_get_y: 'Compra X Lleva Y'
    };
    return types[type] || type;
  };

  const getAppliesToLabel = (appliesTo) => {
    const labels = {
      specific_articles: 'Artículos específicos',
      category: 'Categoría',
      all: 'Todos',
      store: 'Tienda'
    };
    return labels[appliesTo] || appliesTo;
  };

  const isActive = offer.active;
  const isExpired = new Date(offer.end_date) < new Date();
  const isUpcoming = new Date(offer.start_date) > new Date();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        background: 'linear-gradient(180deg, #fdfcfb 0%, #f8f7f5 100%)',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        opacity: isActive ? 1 : 0.7,
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Badge de estado en esquina superior derecha */}
      <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
        {isExpired ? (
          <Chip label='Expirada' size='small' color='error' />
        ) : isUpcoming ? (
          <Chip label='Próxima' size='small' color='info' />
        ) : isActive ? (
          <Chip label='Activa' size='small' color='success' />
        ) : (
          <Chip label='Inactiva' size='small' color='default' />
        )}
      </Box>

      <CardContent sx={{ pb: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Icono y título */}
        <Box display='flex' alignItems='flex-start' gap={1.5} mb={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <OfferIcon />
          </Box>
          <Box flex={1} mr={6}>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 0.5 }}>
              {offer.name}
            </Typography>
            {offer.code && (
              <Chip
                label={offer.code}
                size='small'
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>
        </Box>

        {/* Descripción */}
        {offer.description && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.5em'
            }}
          >
            {offer.description}
          </Typography>
        )}

        {/* Tipo y valor de descuento */}
        <Box display='flex' gap={1} mb={2} flexWrap='wrap'>
          <Chip
            icon={<PercentIcon />}
            label={getTypeLabel(offer.type)}
            size='small'
            color='secondary'
            variant='outlined'
          />
          {offer.discount_percentage > 0 && (
            <Chip
              label={`${offer.discount_percentage}% OFF`}
              size='small'
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          )}
          {offer.discount_amount > 0 && (
            <Chip
              label={`$${offer.discount_amount} OFF`}
              size='small'
              sx={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          )}
        </Box>

        {/* Ámbito de aplicación */}
        <Typography variant='caption' color='text.secondary' display='block' mb={1}>
          Aplica a: {getAppliesToLabel(offer.applies_to)}
        </Typography>

        {/* Vigencia */}
        <Box display='flex' flexDirection='column' gap={0.5} mb={1}>
          <Typography variant='caption' color='text.secondary'>
            Desde: {format(new Date(offer.start_date), 'dd MMM yyyy', { locale: es })}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Hasta: {format(new Date(offer.end_date), 'dd MMM yyyy', { locale: es })}
          </Typography>
        </Box>

        {/* Límites de uso */}
        {(offer.max_uses_total > 0 || offer.current_uses > 0) && (
          <Box display='flex' gap={1} alignItems='center' mt={1}>
            <Typography variant='caption' sx={{ fontWeight: 600 }}>
              Usos: {offer.current_uses}
              {offer.max_uses_total > 0 && ` / ${offer.max_uses_total}`}
            </Typography>
          </Box>
        )}

        {/* Aplicación automática */}
        {offer.apply_automatically && (
          <Chip
            label='Automática'
            size='small'
            color='info'
            variant='outlined'
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
        <Box display='flex' alignItems='center'>
          <Typography variant='caption' sx={{ mr: 1, color: 'text.secondary' }}>
            {isActive ? 'Activa' : 'Inactiva'}
          </Typography>
          <Switch
            checked={isActive}
            onChange={() => onToggleActive(offer._id)}
            size='small'
            color='success'
          />
        </Box>

        <Box display='flex' gap={0.5}>
          <Tooltip title='Estadísticas'>
            <IconButton
              size='small'
              color='info'
              onClick={() => onViewStats && onViewStats(offer)}
            >
              <ReportIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          
          <Tooltip title='Editar'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => onEdit(offer)}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip title='Eliminar'>
            <IconButton
              size='small'
              color='error'
              onClick={() => onDelete(offer._id)}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

export default OfferCard;
